import {
    addBreadcrumb as addSentryBreadcrumb,
    BrowserClient,
    defaultStackParser,
    ErrorEvent,
    EventHint,
    getDefaultIntegrations,
    makeFetchTransport,
    Scope,
    SeverityLevel,
} from '@sentry/react';
import { PlatformEnvironment } from '@mews/enums';
import { SENTRY_ERROR_FILTERS, SENTRY_WARNING_FILTERS } from '../../constants';
import {
    AddBreadcrumbProps,
    LogData,
    LogOptions,
    SentryReporterService,
    SentryReporterServiceParameters,
    SetSentryTagsProps,
    SetSentryUserProps,
} from './types';
import { getErrorRequestFingerprint, parseEventMessage } from './utils';

const severityMap: Record<LogOptions['severity'], SeverityLevel> = {
    warning: 'warning',
    error: 'error',
};

const isWarningAndShouldBeIgnored = (level?: SeverityLevel, errorMessage?: string): boolean =>
    level === severityMap.warning &&
    errorMessage != null &&
    SENTRY_WARNING_FILTERS.some((pattern) => pattern.test(errorMessage));

export const beforeSendHook = (event: ErrorEvent, hint?: EventHint) => {
    const exception = hint?.originalException;

    try {
        const { extra } = event;
        const message = parseEventMessage(event);

        if (isWarningAndShouldBeIgnored(event.level, message)) {
            return null;
        }

        return {
            ...event,
            /**
             * Extend the default fingerprint to diffentiate between different request errors. Unfortunately, in many cases, unrelated errors are grouped together. We can't use error.name because it's translated.
             *
             * In many cases, stacktrace would be enough to differentiate them, but for some reason, Safari doesn't provide all of it, just the generic parts which are the same for all request errors.
             *
             * We can't use custom server side fingerprinting because the necessary data (see inside of `getErrorRequestFingerprint`) is not available there.
             *
             * Another possible alternative would be too use withScope and setFingerprint, but that doesn't really fit into how we use Sentry currently and gives maybe too much control outside of this module.
             *
             * https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/fingerprinting/#group-errors-with-greater-granularity
             */
            fingerprint: ['{{ default }}', ...getErrorRequestFingerprint(exception)],
            message,
            extra: {
                ...extra,
                // Unhandled exception is populated with the original Error in case it has not been handled and reported manually to sentry
                unhandledError: hint && hint.originalException,
            },
        };
    } catch (e) {
        return event;
    }
};

const createSentryReporterService = ({
    dsn,
    environment,
    release,
}: SentryReporterServiceParameters): SentryReporterService => {
    const scope = new Scope();

    scope.setClient(
        new BrowserClient({
            dsn,
            environment,
            release,
            ignoreErrors: [...SENTRY_ERROR_FILTERS],
            beforeSend: beforeSendHook,
            normalizeDepth: 4,
            transport: makeFetchTransport,
            stackParser: defaultStackParser,
            integrations: getDefaultIntegrations({ defaultIntegrations: false }),
            tracesSampleRate: environment === PlatformEnvironment.Production ? 1 : 0,
        })
    );

    const log = ({ error, extra = {} }: LogData, { severity }: LogOptions) => {
        scope.setExtras({
            handledError: error,
            extra,
        });

        const level = severityMap[severity];
        const hint: EventHint = {
            captureContext: {
                level,
            },
        };

        if (error instanceof Error) {
            scope.captureException(error, hint);
        } else {
            scope.captureMessage(error, level, hint);
        }
    };

    const logError = (data: LogData) => {
        log(data, { severity: 'error' });
    };

    const logWarning = (data: LogData) => {
        log(data, { severity: 'warning' });
    };

    const setTags = (tags: SetSentryTagsProps) => {
        scope.setTags(tags);
    };

    const setUser = (sentryUser: SetSentryUserProps) => {
        scope.setUser(sentryUser);
    };

    const addBreadcrumb = ({ message }: AddBreadcrumbProps) => {
        addSentryBreadcrumb({
            category: 'action',
            message,
            level: 'info',
        });
    };

    return {
        addBreadcrumb,
        logError,
        logWarning,
        setTags,
        setUser,
    };
};

export default createSentryReporterService;
